import moment from "moment-timezone";
import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import { formatDateAsOrdinal, formatDateAsOrdinalWithMonth } from "../../lib/ordinal";
import { getUserTimeZone } from "../../lib/timeZone";
import { TTypedTFunction } from "@lib/useTypedTranslation";
import { AxisItemLocation } from "@amcharts/amcharts4/.internal/charts/axes/Axis";
import { getRelativeDailyDeviceDateParams, getRelativeHourlyDeviceDateParams } from "@lib/dateParams";
import { highlightDays } from "./highlightDays";
import { XAxisTypes, StackedColumnsChartSeries, YAxisTypes } from "./stackedColumnsChart";

export enum ChartTimePeriod {
  last30Days = 'last30Days',
  last60Days = 'last60Days',
  last90Days = 'last90Days',
  last7Days = 'last7Days',
  last48Hours = 'last48Hours'
}

export function formatDateAxisLabelsInTimeZone(xAxis: am4charts.DateAxis, timeZone?: string) {
  if (!timeZone) {
    return;
  }
  xAxis.renderer.labels.template.adapter.add('textOutput', (text, target) => {
    const date = (target.dataItem as any)?.date ?? (target.dataItem as any)?.dates?.date;
    return date ? moment.utc(date).tz(timeZone).format('HH:mm') : text;
  });
}

// amCharts' own inline text formatting syntax for an italicised axis title - matches what
// deviceAnomaliesDataUsage.tsx's own drawXAxis previously applied inline for its timezone title.
// Shared here so every chart's "Device Local Time (...)" (or equivalent) x-axis title is
// italicised consistently, rather than repeating the bracket markup at each call site.
export function italicAxisTitle(text: string): string {
  return `[font-style: italic]${text}[/]`;
}

export const getChartTimePeriod = (days: number) => {
  switch (days) {
  case 30:
    return ChartTimePeriod.last30Days;
  case 60:
    return ChartTimePeriod.last60Days;
  case 90:
    return ChartTimePeriod.last90Days;
  case 7:
    return ChartTimePeriod.last7Days;
  case 2:
    return ChartTimePeriod.last48Hours;
  }
};

export const getDaysOfChartTimePeriod = (chartTimePeriod: ChartTimePeriod) => {
  switch (chartTimePeriod) {
  case ChartTimePeriod.last30Days:
    return 30;
  case ChartTimePeriod.last60Days:
    return 60;
  case ChartTimePeriod.last90Days:
    return 90;
  case ChartTimePeriod.last7Days:
    return 7;
  case ChartTimePeriod.last48Hours:
    return 2;
  }
};

const createAxisRanges = (rangeLengthsInDays: number, chartStartDate: string, xAxis: am4charts.CategoryAxis, t: TTypedTFunction) => {
  let currentStartDate = chartStartDate;
  const timeZone = getUserTimeZone();
  const nowInTimeZone = moment.utc().tz(timeZone);
  const nowWithUtcOffset = nowInTimeZone.add(nowInTimeZone.utcOffset(), 'minutes').valueOf();
  const localNowAsUtcFormat = moment.utc(nowWithUtcOffset).valueOf();

  while (moment.utc(parseInt(currentStartDate)).isSameOrBefore(localNowAsUtcFormat)) {
    const isToday = moment.utc(parseInt(currentStartDate)).isSame(localNowAsUtcFormat, 'day');
    const range = xAxis.axisRanges.create();
    range.category = currentStartDate;
    range.endCategory = moment.utc(parseInt(currentStartDate)).add(rangeLengthsInDays, 'days').valueOf().toString();
    range.label.text = isToday ? t('TODAY', { ns: 'timeState' }) : formatDateAsOrdinalWithMonth(parseInt(currentStartDate));
    range.label.disabled = false;
    range.label.location = 0.05 as AxisItemLocation;
    range.grid.strokeOpacity = 0;

    currentStartDate = moment.utc(parseInt(currentStartDate)).add(rangeLengthsInDays, 'days').valueOf().toString();
  }
};

export function drawXAxisLabelsToToday(chart: am4charts.XYChart, xAxis: am4charts.CategoryAxis, days: number, t: TTypedTFunction) {
  if (days === 30) {
    xAxis.renderer.labels.template.adapter.add("textOutput", (text) => {
      if (text) {
        const dateAsNumber = parseInt(text);
        const timeZone = getUserTimeZone();
        const nowInTimeZone = moment.utc().tz(timeZone);
        const nowWithUtcOffset = nowInTimeZone.add(nowInTimeZone.utcOffset(), 'minutes').valueOf();
        const localNowAsUtcFormat = moment.utc(nowWithUtcOffset).valueOf();
        const isToday = moment.utc(dateAsNumber).isSame(localNowAsUtcFormat, 'day');
        return isToday ? t('TODAY', { ns: 'timeState' }) : formatDateAsOrdinal(dateAsNumber);
      }
    });
  } else {
    if (chart.data.length) {
      const startDate = chart.data[0].date;
      createAxisRanges(7, startDate, xAxis, t);
    }
    xAxis.renderer.labels.template.adapter.add("textOutput", (text) => {
      const nonFormattedDates = chart.data.map(datum => datum.date);
      if (!nonFormattedDates.includes(text)) {
        return text;
      }
    });
  }
}

export function drawSelectedDateXAxisLabels(xAxis: am4charts.CategoryAxis, selectedDate: number, categoryIntervalInMinutes: number) {
  xAxis.renderer.labels.template.disabled = true;
  const startOfDay = moment.utc(selectedDate).startOf('day').valueOf();

  let currentStartDate = startOfDay;

  while (moment.utc(currentStartDate).isSameOrBefore(moment.utc(selectedDate).endOf('day').valueOf() + 1)) {

    const range = xAxis.axisRanges.create();
    range.label = new am4charts.AxisLabel();
    range.category = moment.utc(currentStartDate).format('HH:mm');
    range.endCategory = moment.utc(currentStartDate).add(1, 'hour').format('HH:mm');
    range.label.text = moment.utc(currentStartDate).format('HH:mm');
    range.label.disabled = false;
    range.label.location = 0.035 as AxisItemLocation;
    range.label.dx = -25;
    range.grid.strokeOpacity = 0;

    /* we use a category axis, with each date set to a category. the start date is 00:00, as is the last date
     so we need to ensure the last 00:00 is set to a category at the end of the scale */
    if (moment.utc(moment.utc(selectedDate).endOf('day').valueOf() + 1).isSame(currentStartDate)) {
      range.category = moment.utc(selectedDate).endOf('day').subtract(categoryIntervalInMinutes - 1, 'minute').format('HH:mm');
      range.endCategory = moment.utc(selectedDate).endOf('day').subtract(categoryIntervalInMinutes - 1, 'minute').format('HH:mm');
    }

    currentStartDate = moment.utc(currentStartDate).add(2, 'hours').valueOf();

  }
}

/* istanbul ignore next */
export function addLink(series: am4charts.ColumnSeries, link: string) {
  series.columns.template.events.on("over", (event: any) => {
    let utc: number | string | undefined;
    const dataItem = event.target?.dataItem;

    if (!dataItem) return;

    if (dataItem?.dates?.dateX) { // DateAxis
      utc = dataItem.dates.dateX.valueOf();
    } else if (dataItem?.categories?.categoryX) { // Category Axis
      utc = dataItem.categories.categoryX;
    }
    series.columns.template.url = `${link}&utc=${utc}`;
  });
  return series;
}

export function addLegend(chart: am4charts.XYChart, invertedXAxis?: boolean) {
  chart.legend = new am4charts.Legend();
  chart.legend.useDefaultMarker = true;
  chart.legend.labels.template.fontWeight = '400';
  chart.legend.labels.template.dy = -8;
  if (invertedXAxis) {
    chart.legend.marginTop = 20;
  }

  const markerTemplate = chart.legend.markers.template;
  markerTemplate.width = 10;
  markerTemplate.height = 29;

  markerTemplate.disposeChildren();
  const legendItem = markerTemplate.createChild(am4core.Rectangle);
  legendItem.fill = am4core.color('#2c678c');
  legendItem.width = 10;
  legendItem.height = 10;

  /* istanbul ignore next */
  legendItem.adapter.add("fill", function (_, target) {
    if (target.dataItem && target.dataItem.dataContext && (target.dataItem.dataContext as any).dummyData) {
      return (target.dataItem.dataContext as any).dummyData.colour;
    }
  });

  chart.legend.itemContainers.template.clickable = false;
  chart.legend.itemContainers.template.focusable = false;
  chart.legend.itemContainers.template.cursorOverStyle = am4core.MouseCursorStyle.default;
}

export function drawXAxisLabelsFromDateRange(xAxis: am4charts.DateAxis, dateRange: { from: number, to: number }) {
  const days = moment.utc(dateRange.to).diff(moment.utc(dateRange.from), 'days') + 1;

  xAxis.dateFormatter.utc = true;
  xAxis.dateFormats.setKey("day", days === 30 ? "dt" : "MMM dt");
  xAxis.markUnitChange = false;

  xAxis.renderer.labels.template.disabled = false;
  xAxis.renderer.labels.template.location = 0.5;
  xAxis.baseInterval = { timeUnit: "day", count: 1 };

  xAxis.min = moment.utc(dateRange.from).valueOf();
  xAxis.max = moment.utc(dateRange.to).valueOf();

  if (days === 60 || days === 90) {
    xAxis.gridIntervals.setAll([
      { timeUnit: "day", count: 7 },
      { timeUnit: "day", count: 14 }
    ]);
  }

  if (days === 30 || days === 60 || days === 90) {
    weekendsHighlightDateAxis(dateRange.from, dateRange.to, xAxis);
  }

  if (days === 7 || days === 2) {
    xAxis.baseInterval = { timeUnit: "hour", count: 1 };

    if (days !== 7) {
      xAxis.gridIntervals.setAll([
        { timeUnit: "hour", count: 2 },
        { timeUnit: "hour", count: 3 },
        { timeUnit: "hour", count: 6 },
      ]);
    }

    highlightDays(xAxis, dateRange.from, dateRange.to);
  }

  if (days === 1) {
    xAxis.baseInterval = { timeUnit: "hour", count: 1 };
    xAxis.gridIntervals.setAll([
      { timeUnit: "hour", count: 1 },
      { timeUnit: "hour", count: 2 },
      { timeUnit: "hour", count: 3 },
      { timeUnit: "hour", count: 6 },
    ]);
  }
}

export function drawRelativeXAxisLabels(xAxis: am4charts.DateAxis, days: number, timeZoneOffset: number) {
  const dateRange = (days <= 7) ?
    getRelativeHourlyDeviceDateParams(moment.utc(), timeZoneOffset, days)
    :
    getRelativeDailyDeviceDateParams(moment.utc(), timeZoneOffset, days);

  return drawXAxisLabelsFromDateRange(xAxis, dateRange);
}

export function weekendsHighlightDateAxis(from: number, to: number, xAxis: am4charts.DateAxis) {
  let current = moment.utc(from);
  const endDate = moment.utc(to);

  while (current.isBefore(endDate)) {
    const daysUntilSaturday = (6 - current.isoWeekday());
    const weekendStart = moment(current).add(daysUntilSaturday, 'days');
    const weekendEnd = moment(weekendStart).add(2, 'days');
    if (weekendStart.isBefore(endDate)) {
      const range = xAxis.axisRanges.create();
      range.date = weekendStart.toDate();
      range.endDate = weekendEnd.toDate();
      range.axisFill.fill = am4core.color("#d3d3d3");
      range.axisFill.fillOpacity = 0.2;
      range.grid.strokeOpacity = 0;
    }
    current.add(7, 'days');
  }
}

export const handleChartPlotContainerHitEvents = (eventType: 'hit' | 'doublehit', eventHandler: (data: any) => void, chart: am4charts.XYChart, series: am4charts.ColumnSeries, xAxis: am4charts.DateAxis | am4charts.CategoryAxis) => {
  if (!eventHandler) {
    return;
  }
  chart.plotContainer.events.on(eventType, () => {
    // Skip processing if hit originated from labelBullet by checking custom flag set in labelBullet hit event
    if ((chart as any).skipChartPlotHitEvents && (chart as any).skipChartPlotHitEventsTimeout > moment.utc().valueOf()) {
      (chart as any).skipChartPlotHitEvents = false; // Reset flag for future hits on labelBullet
      return;
    }
    const xPosition = chart.cursor.xPosition;
    // Cursor position is relative to the visible (possibly zoomed) plot area, so it
    // must be converted to an axis position before looking up the data item
    const axisPosition = xAxis.toAxisPosition(xPosition);
    const data = xAxis.getSeriesDataItem(series, axisPosition, true);
    const xAxisValue = data?.dataContext;
    if (xAxisValue !== undefined && xPosition !== undefined) {
      eventHandler(xAxisValue);
    }
  });
};

function configureScrollbarGrip(grip: am4core.ResizeButton, gripColor: string, hoverColor: string, downColor: string) {
  grip.icon.disabled = true;
  grip.background.disabled = true;
  grip.tooltip.disabled = true;

  const rect = grip.createChild(am4core.Rectangle);
  rect.width = 10;
  rect.height = 10;
  rect.fill = am4core.color(gripColor);

  grip.background.fill = am4core.color(gripColor);
  grip.background.states.getKey('hover').properties.fill = am4core.color(hoverColor);
  grip.background.states.getKey('down').properties.fill = am4core.color(downColor);
}

export function configureDragToZoom(chart: am4charts.XYChart, xAxis: am4charts.DateAxis) {
  const cursor = chart.cursor ?? new am4charts.XYCursor();
  chart.cursor = cursor;
  cursor.lineY.disabled = false;
  cursor.lineX.disabled = false;
  cursor.behavior = 'zoomX';
  chart.zoomOutButton.disabled = true;

  xAxis.minZoomCount = 12;

  // This prevents chart dragging when zoomed to minimum zoom level
  xAxis.events.on('startendchanged', () => {
    const dataLength = chart.data.length;
    if (dataLength > 0 && chart.cursor) {
      const range = xAxis.end - xAxis.start;
      const minRange = xAxis.minZoomCount / dataLength;
      if (range <= minRange * 1.05) {
        chart.cursor.lineY.disabled = true;
        chart.cursor.lineX.disabled = true;
        chart.cursor.behavior = 'none';
      } else {
        chart.cursor.lineY.disabled = false;
        chart.cursor.lineX.disabled = false;
        chart.cursor.behavior = 'zoomX';
      }
    }
  });

  chart.scrollbarX = new am4core.Scrollbar();
  chart.scrollbarX.minHeight = 10;
  chart.scrollbarX.marginBottom = 15;
  chart.scrollbarX.parent = chart.bottomAxesContainer;
  chart.scrollbarX.opacity = 1;
  chart.scrollbarX.interactionsEnabled = true;
  chart.scrollbarX.showSystemTooltip = false;

  chart.scrollbarX.background = new am4core.RoundedRectangle();
  chart.scrollbarX.background.cornerRadius(0, 0, 0, 0);
  chart.scrollbarX.background.fill = am4core.color('#C2D1E0');
  chart.scrollbarX.background.fillOpacity = 0.3;

  chart.scrollbarX.thumb.background.fill = am4core.color('#5b9ad2');
  chart.scrollbarX.thumb.background.fillOpacity = 0.5;
  chart.scrollbarX.thumb.background.states.getKey('hover').properties.fill = am4core.color('#4a89c1');
  chart.scrollbarX.thumb.background.states.getKey('down').properties.fill = am4core.color('#3a79b1');
  chart.scrollbarX.thumb.tooltip.disabled = true;

  configureScrollbarGrip(chart.scrollbarX.startGrip, '#5b9ad2', '#4a89c1', '#3a79b1');
  configureScrollbarGrip(chart.scrollbarX.endGrip, '#5b9ad2', '#4a89c1', '#3a79b1');
}

export const createYAxis = (yAxisType: YAxisTypes, useAxisTooltip: boolean) => {
  return (chart: am4charts.XYChart): am4charts.ValueAxis | am4charts.DurationAxis => {
    if (yAxisType === 'duration') {
      const durationAxis = chart.yAxes.push(new am4charts.DurationAxis());
      durationAxis.renderer.grid.template.disabled = true;
      if (useAxisTooltip) {
        durationAxis.tooltip.disabled = true;
      }
      return durationAxis;
    } else {
      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.min = 0;
      valueAxis.renderer.minGridDistance = 30;
      valueAxis.calculateTotals = true;
      valueAxis.contentAlign = 'right';
      if (useAxisTooltip) {
        valueAxis.tooltip.disabled = true;
      }
      return valueAxis;
    }
  };
};
